import type { DateTime } from "luxon";
import { useCallback } from "react";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { findScheduleSlot } from "../departmentWeeks";
import type { WeekData } from "../models";
import { dayIndexOfDate, weekIndexOfDate } from "../panelWeeks";
import { getUniqueLocations } from "../utilFunctions";
import {
	computeDayTargetContext,
	computeLocationTotalTarget,
	countCoverageNursesOnLocation,
	locationHasConfiguredTarget,
	shiftNursesForDay,
} from "./locationTarget";

/**
 * Per-location staffing picture for a single day/shift, as read by the location picker's par pills.
 *
 * `current` is how many staff are ALREADY on the location for those hours (no `+1` — this is a status
 * read, not the "would adding one exceed?" projection that `useStaffingTargetCheck` computes). `par` is the
 * location total target; `hasPar` distinguishes a real "0 required" from an unconfigured location so the
 * latter shows a muted "no par" rather than a red gap; `belowPar` is understaffed vs a configured target.
 */
export type LocationParCount = { current: number; par: number; hasPar: boolean; belowPar: boolean };

export type LocationParCountsParams = { date: DateTime; shiftType: string; isActual: boolean };

/** `weeks` are the caller's own schedule weeks around the dates it asks about. */
export const useLocationParCounts = (weeks: WeekData[]) => {
	const { chosenDepartment } = useChosenDepartment();
	const { isHospitalView } = useDepartmentFlags();

	return useCallback(
		({ date, shiftType, isActual }: LocationParCountsParams): Record<string, LocationParCount> => {
			const context = computeDayTargetContext(weeks, chosenDepartment, weekIndexOfDate(weeks, date), dayIndexOfDate(weeks, date), isHospitalView);
			const slot = findScheduleSlot(weeks, date, shiftType);
			const shiftNurses = shiftNursesForDay(slot && weeks[slot.weekIndex].weeklyShifts[slot.shiftIndex].days[slot.dayIndex], isActual);

			const result: Record<string, LocationParCount> = {};
			for (const name of getUniqueLocations(chosenDepartment.locations, false).map((loc) => loc.location)) {
				const matchingLocations = chosenDepartment.locations.filter((loc) => loc.location === name);
				const hasPar = matchingLocations.some(locationHasConfiguredTarget);
				const par = context ? computeLocationTotalTarget(matchingLocations, context, chosenDepartment.statsMode) : 0;
				const current = countCoverageNursesOnLocation(shiftNurses, name);
				result[name] = { current, par, hasPar, belowPar: hasPar && current < par };
			}
			return result;
		},
		[weeks, chosenDepartment, isHospitalView],
	);
};
